import cloneDeep from 'lodash.clonedeep';
import isEqual from 'lodash.isequal';
import { formatFormDate, getCurrentDate } from '@general/js/utils';
import { OperationType, VeteranCardTypeOptions, NonDependantFlexCodes } from '@/enums';

export const mapMemberInformation = (memberInformation, initialMemberInformation) => {
  const immutableFields = ['firstName', 'lastName'];
  const memberInformationObj = cloneDeep(memberInformation);
  const initialData = cloneDeep(initialMemberInformation);

  if (isEqual(memberInformationObj.residentialAddress, initialData.residentialAddress)) {
    memberInformationObj.residentialAddress.op = OperationType.NONE;
  } else if (!initialData.residentialAddress?.addressLine1 && !isEqual(memberInformationObj.residentialAddress, initialData.residentialAddress)) {
    memberInformationObj.residentialAddress.op = OperationType.ADD;
  } else {
    memberInformationObj.residentialAddress.op = OperationType.REPLACE;
  }

  // remove fields from object as per EAI api requirement for CMS
  immutableFields.forEach((key) => {
    delete memberInformationObj[key];
  });

  // delete state last modified from the address
  delete memberInformationObj.residentialAddress.stateLastModified;

  return memberInformationObj;
};

export const mapContactInformation = (contactInformation, initialContactInformation) => {
  const contactInformationObj = cloneDeep(contactInformation);
  const initialData = cloneDeep(initialContactInformation);

  if (isEqual(contactInformationObj.postalAddress, initialData.postalAddress)) {
    contactInformationObj.postalAddress.op = OperationType.NONE;
  } else if (!initialData.postalAddress?.addressLine1 && !isEqual(contactInformationObj.postalAddress, initialData.postalAddress)) {
    contactInformationObj.postalAddress.op = OperationType.ADD;
  } else {
    contactInformationObj.postalAddress.op = OperationType.REPLACE;
  }

  // delete state last modified from the payload
  delete contactInformationObj.postalAddress.stateLastModified;

  const preferredContactMethodsIndexZero = contactInformationObj.preferredContactMethods[0];
  contactInformationObj.preferredPhoneNumber = '';
  if (preferredContactMethodsIndexZero !== undefined) {
    contactInformationObj.preferredPhoneNumber = preferredContactMethodsIndexZero.number;
  }

  delete contactInformationObj.preferredContactMethods;
  return contactInformationObj;
};

export const mapConcessionCard = (concessionCard) => {
  let newCardList = cloneDeep(concessionCard.initialCardList);

  if (concessionCard.hasConcessionCard) {
    // any cards that is removed or replaced
    const currentCardList = concessionCard.cardList;

    currentCardList.forEach((cardObj) => {
      // only add new card. new card can be identified as `op` value that is `add`
      if (cardObj.op === OperationType.ADD) {
        newCardList.push(cardObj);
      }
      // update replaced cards. replaced card can be identified as `op` value that is `replace`.
      if (cardObj.op === OperationType.REPLACE) {
        const index = newCardList
          .map((card) => {
            return card.id;
          })
          .indexOf(cardObj.id);
        newCardList[index] = { ...cardObj };
      }
    });

    if (newCardList.length > 0) {
      newCardList = newCardList.map((card) => {
        return { ...card, expiryDate: card.expiryDate ? formatFormDate(card.expiryDate) : '' };
      });
    }
  } else {
    const currentDate = getCurrentDate();
    newCardList = concessionCard.initialActiveCardList.map((cardObj) => {
      return {
        ...cardObj,
        op: OperationType.REPLACE,
        expiryDate: cardObj.expiryDate ? formatFormDate(cardObj.expiryDate) : '',
        endDate: currentDate,
      };
    });
  }

  return { cardList: newCardList, hasConcessionCard: concessionCard.hasConcessionCard, lastModified: concessionCard.lastModified };
};

export const mapPrivacyAccess = (privacyAccess, initialPrivacyAccess) => {
  const privacyAccessObj = cloneDeep(privacyAccess);
  const initialData = cloneDeep(initialPrivacyAccess);

  return {
    op: privacyAccessObj.policyPermission !== initialData.policyPermission ? OperationType.REPLACE : OperationType.NONE,
    policyPermission: privacyAccessObj.policyPermission,
  };
};

export const mapKeepPrivate = (privacyAccess, initialPrivacyAccess) => {
  const privacyAccessObj = cloneDeep(privacyAccess);
  const initialData = cloneDeep(initialPrivacyAccess);

  return {
    op: privacyAccessObj.keepPrivate !== initialData.keepPrivate ? OperationType.REPLACE : OperationType.NONE,
    value: privacyAccessObj.keepPrivate,
  };
};

export const mapVeteranCards = (veteranCard, initialVeteranCard) => {
  const veteranCardObj = cloneDeep(veteranCard);
  const initialVeteranCardObj = cloneDeep(initialVeteranCard);

  veteranCardObj.veteranCardList.forEach((card, index) => {
    // Set OP to NONE for card and conditions if no changes to veteran card object
    if (isEqual(card, initialVeteranCardObj.veteranCardList[index])) {
      veteranCardObj.veteranCardList[index].op = OperationType.NONE;

      // Go through each accepted condition in acceptedConditions and add op NONE
      veteranCardObj.veteranCardList[index].acceptedConditions.forEach((_condition, condIndex) => {
        veteranCardObj.veteranCardList[index].acceptedConditions[condIndex].op = OperationType.NONE;
      });
    }

    // EAI sends us `Veteran Gold Card` on retrieve but exists `GOLD` on submission
    const mappedCardType =
      card.cardType.toLowerCase() === VeteranCardTypeOptions.VETERAN_GOLD_CARD ? VeteranCardTypeOptions.VETERAN_GOLD_CODE : VeteranCardTypeOptions.VETERAN_WHITE_CODE;
    veteranCardObj.veteranCardList[index].cardType = mappedCardType;
  });
  return veteranCardObj;
};

export const mapEligibility = (eligibility, initialEligibility) => {
  let eligibilityObj = cloneDeep(eligibility);
  const initialData = cloneDeep(initialEligibility);
  const noChange = isEqual(eligibilityObj, initialData);

  eligibilityObj = {
    op: noChange ? OperationType.NONE : OperationType.REPLACE,
    ...eligibilityObj,
  };

  return eligibilityObj;
};

export const mapOtherPolicyUpdates = (memberPolicies, address, initialAddress) => {
  const addressObj = cloneDeep(address);
  const initialAddressObj = cloneDeep(initialAddress);
  let otherPolicyUpdatesObj = null;

  // If there is no changes to address postcode or state, return null
  if (addressObj.postcode === initialAddressObj.postcode && addressObj.state === initialAddressObj.state) {
    return otherPolicyUpdatesObj;
  }

  // Filter memberships where the relationship is Policy Holder
  const policyHolderMemberships = memberPolicies.filter((membership) => {
    return membership.relationship === NonDependantFlexCodes.POLICY_HOLDER;
  });

  // If there is only one membership where the relationship is policy holder, no changes required
  if (policyHolderMemberships.length === 1) {
    return otherPolicyUpdatesObj;
  }

  // If there is more than one membership and the member is a policy holder on the memberships
  // Update the other policies' postcode or state
  if (policyHolderMemberships.length > 1) {
    otherPolicyUpdatesObj = {
      memberships: policyHolderMemberships.map((membership) => {
        return {
          memberNumber: membership.memberNumber,
          postcode: addressObj.postcode !== membership.postcode ? addressObj.postcode : null,
          state: addressObj.state !== membership.state ? addressObj.state : null,
        };
      }),
    };
  }

  return otherPolicyUpdatesObj;
};

export const createUpdatedMemberDetailsObj = (memberDetails, initialMemberDetails, otherPolicyUpdates) => {
  const memberDetailsObject = {};

  // filling memberDetails object with updated and required fields for the member details API
  memberDetailsObject.currentDate = getCurrentDate();
  memberDetailsObject.memberInformation = mapMemberInformation(memberDetails.memberInformation, initialMemberDetails.memberInformation);
  memberDetailsObject.contactInformation = mapContactInformation(memberDetails.contactInformation, initialMemberDetails.contactInformation);
  memberDetailsObject.concessionCard = mapConcessionCard(memberDetails.concessionCard);
  memberDetailsObject.studentDeclaration = memberDetails.studentDeclaration;
  memberDetailsObject.eligibility = mapEligibility(memberDetails.eligibility, initialMemberDetails.eligibility);
  memberDetailsObject.veteranCard = mapVeteranCards(memberDetails.veteranCard, initialMemberDetails.veteranCard);
  memberDetailsObject.otherPolicyUpdates = mapOtherPolicyUpdates(
    otherPolicyUpdates,
    memberDetails.memberInformation.residentialAddress,
    initialMemberDetails.memberInformation.residentialAddress,
  );

  // EAI sends { privacyAccess: {keepPrivate, policyPermission} } from Retrieve but expects { privacyAccess: {op, policyPermission}, keepPrivate: {op, value} } on Submit
  memberDetailsObject.privacyAccess = mapPrivacyAccess(memberDetails.privacyAccess, initialMemberDetails.privacyAccess);
  memberDetailsObject.keepPrivate = mapKeepPrivate(memberDetails.privacyAccess, initialMemberDetails.privacyAccess);

  return memberDetailsObject;
};
